import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Save, Loader2, Sparkles, RotateCcw, X, Mail, Type, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";

const CATEGORY_META = {
  email: { label: "Email", icon: Mail, tint: "text-foreground bg-muted/40 border-primary/40" },
  pdf_section: { label: "PDF", icon: FileText, tint: "text-foreground bg-muted border-border" },
  script: { label: "Guião", icon: ClipboardCheck, tint: "text-[#059669] bg-[hsl(var(--success-bg))] border-[#059669]/30" },
};

const inp = "w-full h-11 bg-card border border-border rounded-lg px-3 text-[14px] text-foreground focus:outline-none focus:border-primary";
const ta = "w-full bg-card border border-border rounded-lg px-3 py-2 text-[14px] text-foreground focus:outline-none focus:border-primary font-mono";

const Label = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold mb-1">{children}</label>
);

function TemplateEditor({ tpl, onSave, onClose }) {
  const [content, setContent] = useState(tpl.content || "");
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiIntent, setAiIntent] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...tpl, content };
      const { data } = await api.put(`/templates/${tpl.key}`, payload);
      toast.success("Template guardado");
      onSave(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  const aiRewrite = async () => {
    if (!aiIntent.trim()) { toast.error("Descreva o que quer melhorar"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post(`/templates/${tpl.key}/ai-rewrite`, {
        intent: aiIntent, keep_variables: true,
      });
      setContent(data.content || "");
      setAiOpen(false);
      setAiIntent("");
      toast.success("IA reescreveu o template · reveja e guarde");
    } catch (e) {
      toast.error(e.response?.data?.detail || "IA falhou");
    } finally { setAiBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="tpl-editor" onClick={onClose}>
      <div className="bg-muted/40 border border-border rounded-2xl w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Editar Template</div>
            <h3 className="font-display text-2xl text-foreground">{tpl.name}</h3>
            {tpl.description && <p className="text-[12px] text-muted-foreground mt-1 max-w-lg">{tpl.description}</p>}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="tpl-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {(tpl.variables || []).length > 0 && (
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-foreground font-semibold mb-1.5">Variáveis disponíveis</div>
              <div className="flex flex-wrap gap-1.5">
                {tpl.variables.map((v) => (
                  <button
                    key={v}
                    onClick={() => setContent((c) => c + `{${v}}`)}
                    className="text-[11px] bg-muted border border-border text-[#374151] hover:bg-muted rounded-md px-2 py-1 font-mono">
                    {`{${v}}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Conteúdo</Label>
              <div className="flex items-center gap-2">
                {!aiOpen && (
                  <button onClick={() => setAiOpen(true)} data-testid="tpl-ai-open"
                    className="text-[11px] uppercase tracking-[0.14em] font-semibold px-3 py-1.5 rounded-full bg-foreground hover:bg-foreground/90 text-background inline-flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Reescrever com IA
                  </button>
                )}
                <button onClick={() => setContent(tpl.content || "")} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
                  <RotateCcw className="w-3 h-3" /> Repor
                </button>
              </div>
            </div>
            <textarea rows={12} className={ta} value={content} onChange={(e)=>setContent(e.target.value)} data-testid="tpl-content" placeholder="Escreva o conteúdo do template..." />
          </div>

          {aiOpen && (
            <div className="bg-muted/40 border border-primary/40 rounded-lg p-4 space-y-3" data-testid="tpl-ai-panel">
              <div>
                <Label>O que quer que a IA faça? (intent)</Label>
                <textarea
                  rows={3}
                  data-testid="tpl-ai-intent"
                  value={aiIntent}
                  onChange={(e)=>setAiIntent(e.target.value)}
                  className={ta}
                  placeholder='Ex: "Torna mais calorosa e adiciona um agradecimento pela confiança" ou "Reescreve de forma mais concisa e formal"' />
              </div>
              <div className="flex items-center gap-2">
                <button onClick={aiRewrite} disabled={aiBusy || !aiIntent.trim()} data-testid="tpl-ai-run"
                  className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 disabled:opacity-40 text-background font-semibold rounded-full px-4 py-2 text-sm">
                  {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {aiBusy ? "A pensar..." : "Aplicar reescrita"}
                </button>
                <button onClick={() => { setAiOpen(false); setAiIntent(""); }} className="text-[11px] text-muted-foreground hover:text-foreground">Cancelar</button>
              </div>
              <div className="text-[11px] text-foreground italic">
                💡 A IA preserva os placeholders {`{variavel}`} — só reescreve texto em redor.
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-5 border-t border-border bg-card rounded-b-2xl">
          <div className="text-[11px] text-muted-foreground">
            {tpl.updated_at ? `Atualizado em ${new Date(tpl.updated_at).toLocaleString("pt-PT")}` : "Nunca guardado"}
            {tpl.updated_by ? ` por ${tpl.updated_by.split("@")[0]}` : ""}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-[13px] text-muted-foreground hover:text-foreground px-3 py-2">Fechar</button>
            <button onClick={save} disabled={saving} data-testid="tpl-save"
              className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 disabled:opacity-40 text-background font-semibold rounded-md px-4 py-2 text-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TemplatesSettings() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/templates").then((r) => setTemplates(r.data.items || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const grouped = templates.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="p-8 lg:p-12 max-w-5xl mx-auto" data-testid="templates-root">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Configurações · Templates</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight flex items-center gap-3">
          <Type className="w-8 h-8 text-foreground" /> Templates
        </h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
          Edite os textos padrão de emails, secções de PDF e frases do guião. Use o botão <b>Reescrever com IA</b> para o Claude sugerir variações — respeitando sempre os placeholders <code className="text-[11px] bg-muted px-1 rounded">{`{nome}`}</code>.
        </p>
      </div>

      {loading && <div className="text-muted-foreground">A carregar templates…</div>}

      {!loading && Object.entries(grouped).map(([cat, list]) => {
        const meta = CATEGORY_META[cat] || CATEGORY_META.email;
        const Icon = meta.icon;
        return (
          <div key={cat} className="mb-8" data-testid={`tpl-group-${cat}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-4 h-4 text-foreground" />
              <h2 className="font-display text-xl text-foreground">{meta.label}</h2>
              <span className="text-[11px] text-muted-foreground">({list.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {list.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setEditing(t)}
                  data-testid={`tpl-card-${t.key}`}
                  className="text-left bg-card border border-border rounded-xl p-4 hover:border-primary hover:shadow-md transition-all">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-ui text-[15px] text-foreground font-medium flex-1">{t.name}</div>
                    <span className={`text-[9px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full border ${meta.tint}`}>{meta.label}</span>
                  </div>
                  {t.description && <p className="text-[12px] text-muted-foreground mb-2 line-clamp-2">{t.description}</p>}
                  <div className="text-[11px] text-muted-foreground font-mono truncate">
                    {t.content ? t.content.replace(/<[^>]+>/g, "").slice(0, 100) : <span className="italic">(vazio — usar IA para gerar)</span>}
                  </div>
                  {(t.variables || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {t.variables.map(v => (
                        <span key={v} className="text-[10px] text-foreground bg-muted/40 px-1.5 py-0.5 rounded font-mono">{`{${v}}`}</span>
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {editing && (
        <TemplateEditor
          tpl={editing}
          onClose={() => setEditing(null)}
          onSave={(updated) => {
            setTemplates((all) => all.map(x => x.key === updated.key ? updated : x));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
